/* Serena landing page: motion and interactions.
   GSAP + ScrollTrigger drive the motion; every interactive piece (comparator, product
   switch, tabs, FAQ, callouts, buy bar, back-to-top) also works without GSAP.
   prefers-reduced-motion turns all motion off. */
(function () {
  if (!window.gsap || !window.ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  gsap.defaults({ ease: 'power3.out', overwrite: 'auto' });
  ScrollTrigger.config({ ignoreMobileResize: true });
  var isPhone = window.matchMedia('(max-width: 720px)').matches;

  /* Scrolling stays fully native: no smoothing library, so the page moves exactly as far as the
     wheel, trackpad or finger says and never fights a second swipe. */
  var headerOffset = function () { return (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sr-header-max')) || 0) + 16; };
  /* in-page links glide to their section and stop just below the sticky store header
     (a native smooth scroll: any new wheel or touch input simply takes over) */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('#serena-page a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute('href');
    if (id.length < 2) return;
    var target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - headerOffset(), behavior: 'smooth' });
  });

  /* One-shot reveals use IntersectionObserver so they fire however the visitor arrives
     (anchor jumps, fast flicks, layout shifts from lazy images). */
  var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      var fn = en.target.__reveal; if (fn) { en.target.__reveal = null; fn(); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0 }) : null;
  function whenVisible(el, fn) { if (!el) return; if (!io) { fn(); return; } el.__reveal = fn; io.observe(el); }

  function splitWords(el) {
    var label = el.textContent.replace(/\s+/g, ' ').trim();
    var frag = document.createDocumentFragment();
    Array.prototype.slice.call(el.childNodes).forEach(function (node) {
      if (node.nodeType !== 3) { frag.appendChild(node.cloneNode(true)); return; }
      node.textContent.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
        var mask = document.createElement('span'); mask.className = 'word';
        var inner = document.createElement('span'); inner.textContent = part;
        mask.appendChild(inner); frag.appendChild(mask);
      });
    });
    el.textContent = ''; el.appendChild(frag);
    el.setAttribute('aria-label', label);
    return el.querySelectorAll('.word > span');
  }

  /* ---------- headings ---------- */
  var heroTween = null;
  document.querySelectorAll('[data-anim]').forEach(function (el) {
    if (isPhone && !el.closest('#top')) {
      gsap.set(el, { y: 14, opacity: 0 });
      whenVisible(el, function () { gsap.to(el, { y: 0, opacity: 1, duration: 0.55 }); });
      return;
    }
    var words = splitWords(el), tween;
    if (el.getAttribute('data-anim') === 'mask-words') {
      gsap.set(words, { yPercent: 110 });
      tween = gsap.to(words, { yPercent: 0, duration: 1, stagger: 0.06, paused: true });
    } else {
      gsap.set(words, { y: 22, opacity: 0 });
      tween = gsap.to(words, { y: 0, opacity: 1, duration: 0.7, stagger: 0.035, paused: true });
    }
    if (el.closest('#top')) { heroTween = tween; return; }
    whenVisible(el, function () { tween.play(); });
  });

  /* ---------- hero: the photo is visible on first paint; copy animates over it ---------- */
  var hero = document.querySelector('#top');
  var stage = hero && hero.querySelector('.hero-stage');
  if (hero) {
    var bits = hero.querySelectorAll('.eyebrow, .lede, .hero-cta');
    gsap.set(bits, { y: 20, opacity: 0 });
    if (stage) gsap.fromTo(stage, { scale: 1.06 }, { scale: 1, duration: 2.2, ease: 'power2.out' });
    var intro = gsap.timeline({ delay: 0.1 });
    intro.to(hero.querySelector('.eyebrow'), { y: 0, opacity: 1, duration: 0.6 });
    if (heroTween) intro.add(heroTween.play(), '-=0.3');
    intro.to(hero.querySelectorAll('.lede, .hero-cta'), { y: 0, opacity: 1, duration: 0.7, stagger: 0.1 }, '-=0.5');
    if (isPhone) intro.timeScale(1.6);
  }
  if (stage) {
    var slides = stage.querySelectorAll('.hero-slide');
    var dots = hero.querySelectorAll('.hero-dot');
    var tag = hero.querySelector('.hero-tag');
    var names = ['Cognac', 'Brown', 'Black', 'Beige'];
    var idx = 0, timer = null;
    function show(n) {
      gsap.to(slides[idx], { opacity: 0, duration: 1.1, ease: 'power2.inOut' });
      gsap.fromTo(slides[n], { opacity: 0 }, { opacity: 1, duration: 1.2, ease: 'power2.out' });
      dots.forEach(function (d, i) { d.classList.toggle('is-on', i === n); });
      if (tag) tag.textContent = names[n] || '';
      idx = n;
    }
    function start() { if (slides.length < 2) return; if (!timer) timer = setInterval(function () { show((idx + 1) % slides.length); }, 4200); }
    function stop() { clearInterval(timer); timer = null; }
    ScrollTrigger.create({ trigger: hero, start: 'top bottom', end: 'bottom top', onEnter: start, onEnterBack: start, onLeave: stop, onLeaveBack: stop });
    gsap.to(stage, { yPercent: 10, ease: 'none', scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.4 } });
  }

  /* ---------- statement: words brighten as it passes the middle of the screen ---------- */
  document.querySelectorAll('[data-scrub-words]').forEach(function (el) {
    var words = splitWords(el);
    var targets = Array.prototype.map.call(words, function (w) { return w.parentNode; });
    gsap.to(targets, { color: '#111111', stagger: 0.08, ease: 'none',
      scrollTrigger: { trigger: el, start: 'top 78%', end: 'bottom 42%', scrub: isPhone ? 0.3 : 0.6 } });
  });

  /* ---------- the leather: sticky photo follows the active step ---------- */
  var story = document.querySelector('.story');
  if (story) {
    var sSteps = story.querySelectorAll('.story-step');
    var sBgs = story.querySelectorAll('.story-bg');
    var fades = story.querySelectorAll('.story-sub, .story-end');
    gsap.set(fades, { y: 18, opacity: 0 });
    Array.prototype.forEach.call(fades, function (el) { whenVisible(el, function () { gsap.to(el, { y: 0, opacity: 1, duration: 0.7 }); }); });
    if (isPhone) {
      gsap.set(sSteps, { y: 18, opacity: 0 });
      Array.prototype.forEach.call(sSteps, function (el) { whenVisible(el, function () { gsap.to(el, { y: 0, opacity: 1, duration: 0.7 }); }); });
    }
  }

  /* ---------- Serena at home: hold the section in place and slide the rooms sideways (desktop) ---------- */
  /* pause: the photo drifts slower than the page */
  document.querySelectorAll('.pause').forEach(function (p) {
    var im = p.querySelector('.pause-img');
    if (p.querySelector('[data-cam]') && window.matchMedia('(min-width: 721px) and (min-aspect-ratio: 1/1)').matches) return;   // the room camera moves it instead
    if (im) gsap.fromTo(im, { yPercent: -6 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: p, start: 'top bottom', end: 'bottom top', scrub: 0.4 } });
  });

  var homeSec = document.querySelector('.home');
  var homeStage = homeSec && homeSec.querySelector('.home-stage');
  var homePinned = !!homeStage && window.matchMedia('(min-width: 900px)').matches;
  /* gsap.matchMedia builds the sliding gallery above 900px and fully undoes it below (tablet rotation, resized windows).
     The stage is held by CSS position: sticky inside a taller track, so the browser keeps it in place on its own
     scrolling thread: it never jumps when it starts or stops holding. GSAP only moves the rail sideways. */
  if (homeStage) gsap.matchMedia().add('(min-width: 900px)', function () {
      var rail = homeSec.querySelector('.home-rail');
      var track = homeSec.querySelector('.home-track') || homeStage;
      var bar = homeSec.querySelector('.home-progress span');
      homeSec.classList.add('home--pinned');
      homeSec.querySelectorAll('.home-item img').forEach(function (im) { im.loading = 'eager'; });
      /* Photo size = the height left after the tallest store header, the caption and the progress line.
         Everything is measured, so it adapts to any screen, font size or theme header. */
      var fitHome = function () {
        var first = rail.querySelector('.home-item'); if (!first) return;
        var media = first.querySelector('.home-img');
        var stageH = homeStage.clientHeight;
        var chrome = (homeStage.querySelector('.home-progress').getBoundingClientRect().bottom - rail.getBoundingClientRect().top) - media.offsetHeight;
        var room = stageH - chrome - Math.max(32, stageH * 0.08);
        var w = Math.max(280, Math.min(room * 4 / 3, window.innerWidth * 0.6));
        homeSec.style.setProperty('--sr-home-w', Math.round(w) + 'px');
        /* the track is one stage tall plus the sideways distance, so the stage is held for exactly that long */
        homeSec.style.setProperty('--sr-home-dist', Math.round(dist()) + 'px');
      };
      var dist = function () { return Math.max(0, rail.scrollWidth - window.innerWidth); };
      fitHome();
      ScrollTrigger.addEventListener('refreshInit', fitHome);
      /* hold just under the tallest store header, so the photos are never covered */
      var pinAt = function () { return 'top ' + (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sr-header-max')) || 0) + 'px'; };
      var slide = gsap.to(rail, {
        x: function () { return -dist(); }, ease: 'none',
        scrollTrigger: {
          trigger: track, start: pinAt, end: function () { return '+=' + dist(); },
          scrub: true, invalidateOnRefresh: true
        }
      });
      if (bar) gsap.fromTo(bar, { scaleX: 0 }, { scaleX: 1, ease: 'none',
        scrollTrigger: { trigger: track, start: pinAt, end: function () { return '+=' + dist(); }, scrub: true, invalidateOnRefresh: true } });
      /* each photo drifts a little against the slide, and eases up in size as it arrives */
      homeSec.querySelectorAll('.home-item').forEach(function (item) {
        var img = item.querySelector('img');
        gsap.fromTo(img, { xPercent: -7, scale: 1.14 }, { xPercent: 7, scale: 1.14, ease: 'none',
          scrollTrigger: { trigger: item, containerAnimation: slide, start: 'left right', end: 'right left', scrub: true } });
        gsap.fromTo(item, { opacity: 0.35, y: 24 }, { opacity: 1, y: 0, ease: 'none',
          scrollTrigger: { trigger: item, containerAnimation: slide, start: 'left 95%', end: 'left 55%', scrub: true } });
      });
      return function () {
        ScrollTrigger.removeEventListener('refreshInit', fitHome);
        homeSec.classList.remove('home--pinned');
        homeSec.style.removeProperty('--sr-home-w');
        homeSec.style.removeProperty('--sr-home-dist');
      };
  });

  /* rating bars fill in when the reviews come into view */
  document.querySelectorAll('.rev-bars').forEach(function (list) {
    var bars = list.querySelectorAll('i');
    gsap.set(bars, { '--s': 0 });
    whenVisible(list, function () { gsap.to(bars, { '--s': 1, duration: 1.1, ease: 'power3.out', stagger: 0.08, delay: 0.2 }); });
  });

  /* ---------- one-shot reveals for copy and blocks ---------- */
  var textBits = Array.prototype.filter.call(
    document.querySelectorAll('.sec .eyebrow, .sec .lede, .statement .links, .faq-side .btn'),
    function (el) { return !el.closest('#top') && !el.closest('.story') && !el.closest('.pc') && !el.closest('.dt-panel'); }
  );
  gsap.set(textBits, { y: 18, opacity: 0 });
  textBits.forEach(function (el, i) { whenVisible(el, function () { gsap.to(el, { y: 0, opacity: 1, duration: 0.7, delay: (i % 2) * 0.06 }); }); });

  var trust = document.querySelectorAll('.trust > *');
  gsap.set(trust, { y: 12, opacity: 0 });
  whenVisible(document.querySelector('.trust'), function () { gsap.to(trust, { y: 0, opacity: 1, duration: 0.6, stagger: 0.06, delay: 0.6 }); });

  var blocks = document.querySelectorAll('.num, .pc:not(.pc--more), .cmp-stage, .dt-tabs, ' + (homePinned ? '' : '.home-item, ') + '.rev-score, .rev-feature, .rev-card, .pcard, .faq > details, .about, .more');
  gsap.set(blocks, { y: isPhone ? 20 : 36, opacity: 0 });
  Array.prototype.forEach.call(blocks, function (el, i) {
    whenVisible(el, function () { gsap.to(el, { y: 0, opacity: 1, duration: isPhone ? 0.6 : 0.8, delay: (i % 3) * 0.06 }); });
  });

  document.querySelectorAll('[data-count]').forEach(function (el) {
    var endV = parseFloat(el.getAttribute('data-count')), obj = { v: 0 };
    whenVisible(el, function () { gsap.to(obj, { v: endV, duration: 1.6, ease: 'power2.out',
      onUpdate: function () { el.textContent = String(Math.round(obj.v)); } }); });
  });

  var bar = document.querySelector('.scroll-progress');
  if (bar) gsap.to(bar, { scaleX: 1, ease: 'none', scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 0.3 } });
})();

/* ---------------- Colour comparator ---------------- */
(function () {
  var root = document.querySelector('[data-cmp]');
  if (!root) return;
  var stage = root.querySelector('.cmp-stage'), clip = root.querySelector('.cmp-clip'),
      track = root.querySelector('.cmp-track'), handle = root.querySelector('.cmp-handle');
  var imgs = { a: root.querySelector('.cmp-img-a'), b: root.querySelector('.cmp-img-b') };
  var tags = { a: root.querySelector('.cmp-tag-a'), b: root.querySelector('.cmp-tag-b') };
  var facts = { a: root.querySelector('[data-fact="a"]'), b: root.querySelector('[data-fact="b"]') };
  function setPos(p) {
    p = Math.max(2, Math.min(98, p));
    clip.style.clipPath = 'inset(0 0 0 ' + p + '%)';
    track.style.transform = 'translateX(' + p + '%)';
    handle.setAttribute('aria-valuenow', Math.round(p));
  }
  /* the stage is measured once when a drag starts, never during it */
  var dragging = false, rect = null;
  function posFrom(e) { var r = rect || stage.getBoundingClientRect(); return (e.clientX - r.left) / r.width * 100; }
  stage.addEventListener('pointerdown', function (e) { dragging = true; rect = stage.getBoundingClientRect(); try { stage.setPointerCapture(e.pointerId); } catch (err) {} setPos(posFrom(e)); });
  stage.addEventListener('pointermove', function (e) { if (dragging) setPos(posFrom(e)); });
  ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (t) { stage.addEventListener(t, function () { dragging = false; rect = null; }); });
  handle.addEventListener('keydown', function (e) {
    var cur = parseFloat(handle.getAttribute('aria-valuenow'));
    if (e.key === 'ArrowLeft') { setPos(cur - 5); e.preventDefault(); }
    if (e.key === 'ArrowRight') { setPos(cur + 5); e.preventDefault(); }
    if (e.key === 'Home') { setPos(2); e.preventDefault(); }
    if (e.key === 'End') { setPos(98); e.preventDefault(); }
  });
  var warm = function () { root.querySelectorAll('.swatch-btn[data-side="a"]').forEach(function (b) { var i = new Image(); i.src = b.getAttribute('data-img'); }); };
  if ('IntersectionObserver' in window) {
    var wio = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { wio.disconnect(); warm(); } }, { rootMargin: '600px 0px' });
    wio.observe(root);
  } else warm();
  root.querySelectorAll('.swatch-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var side = btn.getAttribute('data-side');
      root.querySelectorAll('.swatch-btn[data-side="' + side + '"]').forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
      var img = imgs[side];
      var src = btn.getAttribute('data-img'), alt = btn.getAttribute('data-alt'), name = btn.getAttribute('data-name');
      var setText = function () {
        tags[side].textContent = name;
        facts[side].querySelector('h3').textContent = name;
        facts[side].querySelector('p').textContent = btn.getAttribute('data-desc');
        facts[side].querySelectorAll('.cmp-shop b').forEach(function (b) { b.textContent = name; });
        facts[side].querySelectorAll('a[data-k]').forEach(function (a) {
          var k = 'data-v-' + a.getAttribute('data-k');
          if (!btn.hasAttribute(k)) return;
          var v = btn.getAttribute(k);
          a.hidden = !v;
          if (v) a.href = a.href.replace(/\?variant=\d*|$/, '?variant=' + v);
        });
      };
      if (img.getAttribute('src') === src) { setText(); return; }
      setText();
      if (!window.gsap) { img.src = src; img.alt = alt; return; }
      /* crossfade on a layer above, so the old photo never disappears first */
      var old = img.parentNode.querySelector('.cmp-img-next'); if (old) old.remove();
      var next = img.cloneNode(false);
      next.classList.add('cmp-img-next'); next.src = src; next.alt = alt; next.style.opacity = '0';
      img.parentNode.insertBefore(next, img.nextSibling);
      var ready = next.decode ? next.decode().catch(function () {}) : Promise.resolve();
      ready.then(function () {
        if (!next.parentNode) return;
        gsap.to(next, { opacity: 1, duration: 0.45, ease: 'power2.out', onComplete: function () {
          img.src = src; img.alt = alt;
          requestAnimationFrame(function () { next.remove(); });
        } });
      });
    });
  });
  setPos(50);
})();

/* a price written like the store's product cards ($3,099 with raised 99), the plain amount kept for screen readers;
   same markup as snippets/serena-money.liquid */
function serenaMoney(s) {
  var esc = String(s).replace(/[&<>"]/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]; });
  var m = /^(.*)\.(\d\d)$/.exec(esc);
  return m ? '<span class="sr-vh">' + esc + '</span><span aria-hidden="true">' + m[1] + '<sup>' + m[2] + '</sup></span>' : esc;
}

/* ---------------- Product cards: colour dots and chaise / bumper switch update the card in place ----------------
   Each card carries its variants as JSON: { option: { colour: { v: variantId, p: price, s: stock, img: src, z: size } } }.
   z ("W × D in|W × D cm") is per variant in the Shopify build; the static page keeps sizes on the option buttons.
   Colour names come from the dots' labels; data-alt (Shopify build) is the product name for the image alt.
   Shopify build: a (0 = cannot be bought) adds a "Sold out" state, and every text the card can show (stock
   states, colour names, configuration descriptions) is already in the page as hidden spans that are switched,
   so a translation layer (Weglot) translates them once, instead of English being written in by script.
   Nothing navigates until the visitor presses the buy button, which then opens the exact variant. */
(function () {
  var cards = document.querySelectorAll('[data-variants]');
  if (!cards.length) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  cards.forEach(function (card) {
    var data; try { data = JSON.parse(card.getAttribute('data-variants')); } catch (e) { return; }
    var opt = card.getAttribute('data-opt'), col = card.getAttribute('data-col');
    var img = card.querySelector('[data-img]'), price = card.querySelector('[data-price]'), was = card.querySelector('[data-was]'),
        stock = card.querySelector('[data-stock]'), name = card.querySelector('[data-colour-name]'),
        sub = card.querySelector('p[data-sub]'), buys = card.querySelectorAll('[data-buy]'),
        dots = card.querySelectorAll('.pc-dot'), opts = card.querySelectorAll('.seg [data-opt]'),
        sizeIn = card.querySelector('[data-size-in]'), sizeCm = card.querySelector('[data-size-cm]'),
        altBase = card.getAttribute('data-alt'),
        stockStates = stock ? stock.querySelectorAll('[data-st]') : [],
        names = name ? name.querySelectorAll('[data-cn]') : [],
        subs = sub ? sub.querySelectorAll('[data-sub-for]') : [];
    var preloaded = false, token = 0;
    function colourName() {
      var shown = name && name.querySelector('[data-cn="' + col + '"]');
      if (shown) return shown.textContent.trim();
      var dot = card.querySelector('.pc-dot[data-col="' + col + '"]');
      return dot ? dot.getAttribute('aria-label') : col.charAt(0).toUpperCase() + col.slice(1);
    }
    function preload() {
      if (preloaded) return; preloaded = true;
      Object.keys(data).forEach(function (o) { Object.keys(data[o]).forEach(function (c) { new Image().src = data[o][c].img; }); });
    }
    card.addEventListener('pointerenter', preload, { once: true });
    card.addEventListener('focusin', preload, { once: true });
    function render(animate) {
      var d = data[opt] && data[opt][col]; if (!d) return;
      var my = ++token;
      /* always paint the latest selection, even if an earlier photo finishes loading later */
      var swap = function () {
        var cur = data[opt][col];
        img.src = cur.img;
        if (altBase) {
          var g = opts.length > 1 ? card.querySelector('.seg [data-opt="' + opt + '"]') : null;
          img.alt = altBase + (g ? ' (' + g.textContent.trim() + ')' : '') + ' in ' + colourName();
        } else {
          img.alt = img.alt.replace(/ in \w+$/, '') + ' in ' + colourName();
          if (opt === 'chaise' || opt === 'bumper') img.alt = img.alt.replace(/\b(chaise|bumper)\b/i, opt);
        }
      };
      swap();
      /* the fade plays once the new photo has loaded; it is decoration only */
      if (animate && !reduce && img.animate) {
        var fade = function () { if (my === token) img.animate([{ opacity: 0.3, transform: 'scale(0.985)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 380, easing: 'ease-out' }); };
        if (img.complete) fade(); else img.addEventListener('load', fade, { once: true });
      }
      price.innerHTML = serenaMoney(d.p);
      if (was) {
        was.hidden = !d.c;
        was.innerHTML = d.c ? '<span class="sr-vh">Regular price </span>' + serenaMoney(d.c) : '';
        price.classList.toggle('is-sale', !!d.c);
      }
      if (stock) {
        var st = d.a === 0 ? 'out' : d.s > 0 ? 'in' : 'order';
        if (stockStates.length) stockStates.forEach(function (x) { x.hidden = x.getAttribute('data-st') !== st; });
        else stock.textContent = st === 'out' ? 'Sold out' : st === 'in' ? 'In stock · ships from Canada' : 'Available to order';
        stock.classList.toggle('in', st === 'in');
      }
      if (names.length) names.forEach(function (x) { x.hidden = x.getAttribute('data-cn') !== col; });
      else if (name) name.textContent = colourName();
      if (d.z && sizeIn) { var z = d.z.split('|'); sizeIn.textContent = z[0]; if (sizeCm) sizeCm.textContent = z[1] || ''; }
      buys.forEach(function (a) { a.href = a.href.replace(/\?variant=\d+|$/, '?variant=' + d.v); });
      dots.forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-col') === col ? 'true' : 'false');
        b.disabled = !data[opt][b.getAttribute('data-col')];   // this colour is not made in this configuration
      });
      opts.forEach(function (b) {
        var on = b.getAttribute('data-opt') === opt, sz = b.getAttribute('data-size');
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on && sz && sizeIn && sizeCm) { sz = sz.split('|'); sizeIn.textContent = sz[0]; sizeCm.textContent = sz[1] || ''; }
      });
    }
    dots.forEach(function (b) {
      b.addEventListener('click', function () { var c = b.getAttribute('data-col'); if (c === col || !data[opt][c]) return; col = c; render(true); });
    });
    opts.forEach(function (b) {
      b.addEventListener('click', function () {
        var o = b.getAttribute('data-opt'); if (o === opt || !data[o]) return; opt = o;
        if (!data[opt][col]) col = Object.keys(data[opt])[0];
        if (subs.length) subs.forEach(function (x) { x.hidden = x.getAttribute('data-sub-for') !== o; });
        else if (sub && b.getAttribute('data-sub')) sub.textContent = b.getAttribute('data-sub');
        render(true);
      });
    });
  });
})();

/* ---------------- Product cards: keep each row of cards lined up ----------------
   Titles, descriptions and option rows can differ in length (cards built from product data on Shopify, where
   a new product's title may wrap): on computers, every card in a row gets the tallest height of each part. */
(function () {
  var wrap = document.querySelector('#shop .cards');
  if (!wrap) return;
  var parts = ['.pc-head', '.pc-sub', '.pc-opt'];
  function run() {
    var cards = [].slice.call(wrap.querySelectorAll('.pc'));
    cards.forEach(function (c) { parts.forEach(function (sel) { var e = c.querySelector(sel); if (e) e.style.minHeight = ''; }); });
    if (!window.matchMedia('(min-width: 721px)').matches) return;
    var rows = {};
    cards.forEach(function (c) { var k = c.offsetTop; (rows[k] = rows[k] || []).push(c); });
    Object.keys(rows).forEach(function (k) {
      if (rows[k].length < 2) return;
      parts.forEach(function (sel) {
        var els = rows[k].map(function (c) { return c.querySelector(sel); }).filter(Boolean);
        var h = Math.max.apply(null, els.map(function (e) { return e.getBoundingClientRect().height; }));
        els.forEach(function (e) { e.style.minHeight = h + 'px'; });
      });
    });
  }
  run();
  window.addEventListener('resize', run);
  window.addEventListener('load', run);
  wrap.addEventListener('serena:layout', run);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
})();

/* ---------------- Product cards: "Show all" (Shopify build, when the collection has more cards than the first rows) ----------------
   Opening: the list grows to its full height while the new cards rise and fade in one after another.
   Closing: the reverse, and the page glides back to the cards so the visitor is not left far below them. */
(function () {
  var wrap = document.querySelector('#shop .cards'), bar = document.querySelector('#shop .cards-more');
  if (!wrap || !bar) return;
  var btn = bar.querySelector('button'), extra = [].slice.call(wrap.querySelectorAll('.pc--more'));
  if (!btn || !extra.length) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches, busy = false;
  function setOpen(open) {
    wrap.classList.toggle('is-open', open);
    bar.classList.toggle('is-open', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    wrap.dispatchEvent(new CustomEvent('serena:layout'));
  }
  btn.addEventListener('click', function () {
    if (busy) return;
    var opening = !wrap.classList.contains('is-open'), from = wrap.offsetHeight, to;
    if (opening) {
      setOpen(true);
      to = wrap.offsetHeight;
      if (reduce || !window.gsap) return;
      busy = true;
      gsap.fromTo(wrap, { height: from, overflow: 'hidden' }, { height: to, duration: 0.75, ease: 'power3.inOut',
        onComplete: function () { gsap.set(wrap, { clearProps: 'height,overflow' }); busy = false; } });
      gsap.fromTo(extra, { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 0.65, ease: 'power2.out', stagger: 0.07, delay: 0.12,
        clearProps: 'opacity,transform' });
      return;
    }
    /* closing: measure the collapsed height, then animate down to it */
    setOpen(false); to = wrap.offsetHeight; setOpen(true);
    var shop = document.getElementById('shop'), head = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sr-header-max')) || 0;
    var target = wrap.getBoundingClientRect().top + window.scrollY - head - 120;
    var glide = function () { if (window.scrollY > target) window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' }); };
    if (reduce || !window.gsap) { setOpen(false); glide(); return; }
    busy = true;
    glide();
    gsap.to(extra, { opacity: 0, y: 18, duration: 0.3, ease: 'power1.in', stagger: { each: 0.03, from: 'end' } });
    gsap.fromTo(wrap, { height: from, overflow: 'hidden' }, { height: to, duration: 0.65, ease: 'power3.inOut', delay: 0.12,
      onComplete: function () {
        setOpen(false);
        gsap.set(extra, { clearProps: 'opacity,transform' });
        gsap.set(wrap, { clearProps: 'height,overflow' });
        busy = false;
      } });
  });
})();

/* ---------------- Details tabs ---------------- */
(function () {
  var list = document.querySelector('.dt-tabs[role="tablist"]');
  if (!list) return;
  var tabs = list.querySelectorAll('[role="tab"]');
  function select(t, focus) {
    tabs.forEach(function (x) {
      var on = x === t;
      x.setAttribute('aria-selected', on ? 'true' : 'false');
      x.tabIndex = on ? 0 : -1;
      var p = document.getElementById(x.getAttribute('aria-controls'));
      if (p) p.hidden = !on;
      if (on && p && window.gsap && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) gsap.fromTo(p, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.45 });
    });
    if (focus) t.focus();
  }
  tabs.forEach(function (t, i) {
    t.addEventListener('click', function () { select(t); });
    t.addEventListener('keydown', function (e) {
      var n = null;
      if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
      if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
      if (n) { e.preventDefault(); select(n, true); }
    });
  });
})();

/* ---------------- Anatomy callouts ---------------- */
(function () {
  var root = document.querySelector('.ana');
  if (!root) return;
  var cos = root.querySelectorAll('.co'), rows = root.querySelectorAll('.ana-list li'), list = root.querySelector('.ana-list');
  function set(n) {
    cos.forEach(function (c) { c.classList.toggle('is-on', c.getAttribute('data-hot') === n); });
    rows.forEach(function (r) { r.classList.toggle('is-on', r.getAttribute('data-hot') === n); });
    root.classList.toggle('has-on', !!n);
    if (list) list.classList.toggle('has-on', !!n);
  }
  rows.forEach(function (r) {
    r.addEventListener('mouseenter', function () { set(r.getAttribute('data-hot')); });
    r.addEventListener('mouseleave', function () { set(null); });
  });
  if ('IntersectionObserver' in window) {
    var obs = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { obs.disconnect(); root.classList.add('is-in'); } }, { threshold: 0.35 });
    obs.observe(root.querySelector('.ana-photo'));
  } else { root.classList.add('is-in'); }
})();

/* ---------------- FAQ and About: animated open / close ---------------- */
(function () {
  var items = document.querySelectorAll('.faq details');
  if (!items.length) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  items.forEach(function (d) {
    var summary = d.querySelector('summary');
    if (!summary) return;
    var body = document.createElement('div'); body.className = 'faq-body';
    Array.prototype.slice.call(d.childNodes).forEach(function (n) { if (n !== summary) body.appendChild(n); });
    d.appendChild(body);
    body.style.overflow = 'hidden';
    if (!d.open) body.style.height = '0px';
    var busy = false;
    summary.addEventListener('click', function (e) {
      e.preventDefault();
      if (busy) return;
      if (reduce || !window.gsap) { d.open = !d.open; body.style.height = d.open ? 'auto' : '0px'; return; }
      busy = true;
      if (!d.open) {
        d.open = true;
        body.style.height = '0px';
        var h = body.scrollHeight;
        gsap.fromTo(body, { height: 0, opacity: 0 }, { height: h, opacity: 1, duration: 0.42, ease: 'power3.out',
          onComplete: function () { body.style.height = 'auto'; busy = false; if (window.ScrollTrigger) ScrollTrigger.refresh(); } });
      } else {
        gsap.to(body, { height: 0, opacity: 0, duration: 0.3, ease: 'power3.in',
          onComplete: function () { d.open = false; busy = false; if (window.ScrollTrigger) ScrollTrigger.refresh(); } });
      }
    });
  });
  /* "Read about the leather" opens the About panel */
  document.querySelectorAll('a[href="#about"]').forEach(function (a) {
    a.addEventListener('click', function () {
      var d = document.querySelector('#about details');
      if (d && !d.open) d.querySelector('summary').click();
    });
  });
})();

/* ---------------- Back to top ---------------- */
(function () {
  var btn = document.querySelector('.to-top');
  if (!btn) return;
  var shown = false;
  function update() {
    var want = window.scrollY > window.innerHeight * 0.9;
    if (want !== shown) { shown = want; btn.classList.toggle('is-visible', want); }
  }
  window.addEventListener('scroll', update, { passive: true });
  update();
  btn.addEventListener('click', function () {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  });
})();

/* ---------------- Phone buy bar ---------------- */
(function () {
  var bar = document.querySelector('.buy-bar');
  if (!bar) return;
  var mq = window.matchMedia('(max-width: 720px)');
  var hero = document.querySelector('#top');
  var shown = false;
  function update() {
    if (!mq.matches) { if (shown) { shown = false; bar.classList.remove('is-visible'); bar.setAttribute('aria-hidden', 'true'); document.documentElement.classList.remove('sr-bar-on'); } return; }
    var past = hero ? window.scrollY > hero.offsetTop + hero.offsetHeight - 80 : window.scrollY > 600;
    var nearEnd = window.scrollY + window.innerHeight > document.documentElement.scrollHeight - 420;
    var want = past && !nearEnd;
    if (want !== shown) { shown = want; bar.classList.toggle('is-visible', want); bar.setAttribute('aria-hidden', want ? 'false' : 'true'); document.documentElement.classList.toggle('sr-bar-on', want); }
  }
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
})();

/* ---------------- Scroll-scrubbed camera move (leather story, rooms pause) ----------------
   Two layers show exactly the same shot, frame for frame:
     1. the photos, moved with CSS transforms (zoom, pan, cross-fade) from the numbers in data-cam,
        written by tools/story_video.py. They are already on the page, so the camera moves from the very
        first scroll;
     2. the video made from the same numbers. It downloads into memory after the page has loaded (so any
        frame can be shown at once, whatever the server does with range requests) and fades in on top once
        it shows the frame for the current scroll position; because the frames match, the swap is invisible.
   host: element the layers go into; imgs: the photos in shot order (moved into the camera layer);
   vid: optional <video data-src>; boxClass: class of the photo layer; align: 'left' | 'center'. */
function serenaScrubCam(host, cam, imgs, vid, boxClass, align) {
  var box = document.createElement('div'), frame = document.createElement('div'), shots = [];
  box.className = boxClass; frame.className = 'cam-frame';
  frame.style.width = cam.w + 'px'; frame.style.height = cam.h + 'px';
  box.appendChild(frame);
  /* frame (cam.w x cam.h) > one layer per shot (camera move) > mirror layer > the photo, placed on its crop */
  cam.shots.forEach(function (sh, k) {
    var shot = document.createElement('div'), flip = document.createElement('div'), img = imgs[k], s = cam.w / sh.crop[2];
    shot.className = 'cam-shot'; flip.className = 'cam-flip';
    if (sh.flip) flip.style.transform = 'scaleX(-1)';
    img.loading = 'eager'; img.classList.add('cam-img');
    img.style.width = sh.size[0] * s + 'px'; img.style.height = sh.size[1] * s + 'px';
    img.style.left = -sh.crop[0] * s + 'px'; img.style.top = -sh.crop[1] * s + 'px';
    flip.appendChild(img); shot.appendChild(flip); frame.appendChild(shot);
    shots.push(shot);
  });
  host.insertBefore(box, host.firstChild);
  var n = shots.length, gap = cam.seg - cam.fade, total = n * cam.seg - (n - 1) * cam.fade, run = cam.seg - 1 / cam.fps;
  var marks = [0].concat(cam.shots.map(function (s, k) { return k * gap + cam.seg / 2; }), [total]);
  var drawn = -1, ready = false, shown = false, vT = -1, pending = false;
  function fit() {
    /* same geometry as the video: the kept part of the frame covers the box */
    var bw = box.clientWidth, bh = box.clientHeight, k = Math.max(bw / cam.keep, bh / cam.h);
    var dx = align === 'center' ? (bw - cam.keep * k) / 2 : 0;
    frame.style.transform = 'translate(' + dx + 'px,' + (bh - cam.h * k) / 2 + 'px) scale(' + k + ')';
    drawn = -1; vT = -1;
  }
  function draw(t) {
    for (var k = 0; k < n; k++) {
      var sh = cam.shots[k], el = shots[k], local = t - k * gap;
      var on = k === 0 ? local <= cam.seg : (local >= 0 && (local <= cam.seg || k === n - 1));
      if (!on) { if (el.__o !== 0) { el.style.opacity = 0; el.__o = 0; } continue; }
      var p = Math.min(1, Math.max(0, local / run));
      var z = sh.z[0] + (sh.z[1] - sh.z[0]) * p;
      var lx = sh.look[0][0] + (sh.look[1][0] - sh.look[0][0]) * p, ly = sh.look[0][1] + (sh.look[1][1] - sh.look[0][1]) * p;
      var x = Math.max(0, Math.min(cam.w - cam.w / z, lx * cam.w - cam.w / z / 2));
      var y = Math.max(0, Math.min(cam.h - cam.h / z, ly * cam.h - cam.h / z / 2));
      var o = k === 0 ? 1 : Math.min(1, local / cam.fade);
      el.style.transform = 'scale(' + z + ') translate(' + (-x) + 'px,' + (-y) + 'px)';
      if (el.__o !== o) { el.style.opacity = o; el.__o = o; }
    }
  }
  function seek(t) {
    t = Math.min(t, (vid.duration || 1e9) - 0.05);
    if (Math.abs(t - vT) < 0.02) return;
    vT = t;
    if (vid.seeking) { pending = true; return; }   // apply only the latest position once the decoder is free
    vid.currentTime = t;
  }
  var target = 0;
  function update(t) {
    target = t;
    if (!shown && Math.abs(t - drawn) > 0.001) { drawn = t; draw(t); }
    if (ready) seek(t);
  }
  if (vid && vid.canPlayType && vid.canPlayType('video/mp4') !== '') {
    vid.addEventListener('seeked', function () {
      if (pending) { pending = false; vid.currentTime = vT; return; }
      if (ready && !shown) { shown = true; host.closest('.pause, .story-media').classList.add('has-video'); }
    });
    vid.addEventListener('loadeddata', function () {
      var go = function () { ready = true; vT = -1; seek(target); };
      /* Safari (iPad) only paints seeked frames of a video that has played once: a silent play/pause primes it */
      var p = vid.play && vid.play();
      if (p && p.then) p.then(function () { vid.pause(); go(); }, go); else go();
    });
    var load = function () {
      var url = vid.getAttribute('data-src');
      var direct = function () { if (!vid.src) { vid.preload = 'auto'; vid.src = url; vid.load(); } };
      if (window.fetch && window.URL && URL.createObjectURL) {
        fetch(url).then(function (r) { if (!r.ok) throw new Error(r.status); return r.blob(); })
          .then(function (b) { vid.src = URL.createObjectURL(b); vid.load(); }, direct);
      } else direct();
    };
    /* start once the page itself has finished loading, so the video never competes with the first screen */
    var start = function () { (window.requestIdleCallback || function (f) { setTimeout(f, 300); })(load); };
    if (document.readyState === 'complete') start(); else window.addEventListener('load', start);
  }
  return { fit: fit, update: update, marks: marks, total: total };
}

/* ---------------- The leather: active step + photo (or video frame) follow the scroll ----------------
   Picks the step nearest the middle of the screen on every scroll frame, so a fast
   flick or an anchor jump always lands on the right photo. Works without GSAP. */
(function () {
  var story = document.querySelector('.story');
  if (!story) return;
  var steps = story.querySelectorAll('.story-step'), bgs = story.querySelectorAll('.story-bg'),
      head = story.querySelector('.story-head'), beats = story.querySelectorAll('.story-beats i'),
      count = story.querySelector('.story-count b'), cur = -1;
  /* Desktop: the captions are held over the full-screen camera and shown one at a time, like subtitles
     (CSS .is-captions). The scroll distance comes from the beats, one per caption: the intro, then the
     four steps. Phones keep the plain list (the beats are hidden there). */
  var caps = head ? [head].concat(Array.prototype.slice.call(steps)) : [];
  if (head && beats.length === caps.length) story.classList.add('is-captions');
  var capMode = false, hold0 = 0, hold1 = 1;
  function capAt(k) { return hold0 + (hold1 - hold0) * (k + 0.5) / caps.length; }      // scroll position of caption k's middle
  function activate(i) {
    if (i === cur) return;
    cur = i;
    var step = capMode ? i - 1 : i;
    if (capMode) caps.forEach(function (c, k) { c.classList.toggle('is-active', k === i); });
    else steps.forEach(function (s, k) { s.classList.toggle('is-active', k === step); });
    bgs.forEach(function (b, k) { b.classList.toggle('is-on', k === Math.max(0, step)); });
    if (count) count.textContent = '0' + (Math.max(0, step) + 1);
  }
  /* Step centres are measured once (and again on resize / layout changes), so the scroll
     handler only compares numbers and never forces the browser to lay the page out mid-scroll. */
  var centres = [], half = window.innerHeight * 0.5;
  function measure() {
    var y = window.scrollY;
    half = window.innerHeight * 0.5;
    capMode = beats.length === caps.length && getComputedStyle(beats[0]).display !== 'none';
    centres = Array.prototype.map.call(steps, function (s) { var r = s.getBoundingClientRect(); return r.top + y + r.height / 2; });
    if (capMode) {
      /* the captions are held from the moment the section reaches the top of the screen until its end
         reaches the bottom; that stretch is split evenly between the captions */
      var sr = story.getBoundingClientRect();
      hold0 = sr.top + y; hold1 = hold0 + sr.height - window.innerHeight;
    }
    cur = -1;
    if (sc) { vAnchors(); sc.fit(); }
    pick();
  }
  /* Desktop: the photos turn into a moving camera, driven straight by the scroll (no easing), through
     serenaScrubCam below: the photos move at once, the matching video takes over when it has loaded.
     Each step sits on the middle of its own shot; the cross-fades fall between steps.
     Phones and reduced-motion users keep the plain photo swap and never download the video. */
  var media = story.querySelector('.story-media'), cam = null;
  try { cam = JSON.parse(media && media.getAttribute('data-cam') || 'null'); } catch (e) { cam = null; }
  var sc = (cam && cam.shots.length === bgs.length && bgs.length === steps.length &&
            window.matchMedia('(min-width: 721px)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ? serenaScrubCam(media, cam, Array.prototype.slice.call(bgs), story.querySelector('.story-video'), 'story-cam', 'left') : null;
  if (sc) media.classList.add('has-cam');
  var marks = sc ? sc.marks : [], vYs = [];
  function vAnchors() {
    var y = window.scrollY, r = story.getBoundingClientRect(), top = r.top + y;
    /* from the moment the section enters the screen until it has fully left it, so the camera is
       always moving while any of it is visible */
    var stepCentres = capMode ? steps.length ? Array.prototype.map.call(steps, function (s, k) { return capAt(k + 1) + half; }) : [] : centres;   // the intro has no shot of its own
    vYs = [top - window.innerHeight].concat(stepCentres.map(function (c) { return c - half; }), [top + r.height]);
    for (var i = 1; i < vYs.length; i++) if (vYs[i] < vYs[i - 1]) vYs[i] = vYs[i - 1];
  }
  function timeAt(y) {
    if (y <= vYs[0]) return marks[0];
    for (var i = 1; i < vYs.length; i++) {
      if (y <= vYs[i]) { var span = vYs[i] - vYs[i - 1] || 1; return marks[i - 1] + (marks[i] - marks[i - 1]) * (y - vYs[i - 1]) / span; }
    }
    return marks[marks.length - 1];
  }
  function moveCamera() { if (sc) sc.update(timeAt(window.scrollY)); }
  function pick() {
    moveCamera();
    if (capMode) {
      var k = Math.floor((window.scrollY - hold0) / (hold1 - hold0 || 1) * caps.length);
      activate(k < 0 ? 0 : k >= caps.length ? caps.length - 1 : k);
      return;
    }
    var mid = window.scrollY + half, best = 0, bestD = Infinity;
    for (var i = 0; i < centres.length; i++) {
      var d = Math.abs(centres[i] - mid);
      if (d < bestD) { bestD = d; best = i; }
    }
    activate(best);
  }
  window.addEventListener('scroll', pick, { passive: true });
  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(story);
  if (window.ScrollTrigger) ScrollTrigger.addEventListener('refresh', measure);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  measure();
})();

/* ---------------- Pause: one corner sectional, four rooms ----------------
   Desktop: the stage fills the screen and is held at the top (CSS sticky inside a taller track, the store
   header sits over its top edge, like the leather story); the camera moves through the rooms while it is
   held, starting a little before and ending a little after.
   Phones, portrait tablets and reduced motion keep the single photo: a full-height 16:9 frame would crop
   the sofa too tightly on a portrait screen. */
(function () {
  if (!window.matchMedia('(min-width: 721px) and (min-aspect-ratio: 1/1)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('.pause').forEach(function (sec) {
    var stage = sec.querySelector('.pause-stage'), track = sec.querySelector('.pause-track'), cam = null;
    try { cam = JSON.parse(stage && stage.getAttribute('data-cam') || 'null'); } catch (e) { cam = null; }
    var imgs = [sec.querySelector('.pause-img')].concat(Array.prototype.slice.call(sec.querySelectorAll('.pause-room')));
    if (!cam || !track || !imgs[0] || cam.shots.length !== imgs.length) return;
    sec.classList.add('has-cam');
    var sc = serenaScrubCam(stage, cam, imgs, sec.querySelector('.pause-video'), 'pause-cam', 'center');
    var y0 = 0, y1 = 1;
    function update() {
      var p = (window.scrollY - y0) / (y1 - y0);
      sc.update((p < 0 ? 0 : p > 1 ? 1 : p) * sc.total);
    }
    function measure() {
      var y = window.scrollY, r = track.getBoundingClientRect(), vh = window.innerHeight;
      var held = r.top + y, freed = held + r.height - stage.offsetHeight;
      y0 = held - vh * 0.25; y1 = freed + vh * 0.25;      // the first room is mid-move as the stage locks, the last as it lets go
      sc.fit(); update();
    }
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(track);
    if (window.ScrollTrigger) ScrollTrigger.addEventListener('refresh', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    measure();
  });
})();

/* ---------------- Serena at home: previous / next buttons for the room rail ---------------- */
(function () {
  var rail = document.querySelector('.home-rail');
  if (!rail) return;
  document.querySelectorAll('.home-btn').forEach(function (b) {
    b.addEventListener('click', function () {
      var item = rail.querySelector('.home-item');
      var step = item ? item.getBoundingClientRect().width + 24 : rail.clientWidth * 0.8;
      rail.scrollBy({ left: step * parseInt(b.getAttribute('data-dir'), 10), behavior: 'smooth' });
    });
  });
})();

/* ---------------- Resizes and rotations that change the layout ----------------
   The leather story and the rooms pause choose their layout once, when the page loads: on computers and
   landscape tablets their photos move into a scroll camera, which cannot simply be undone. If the window is
   then resized, or a tablet turned, so that the other layout applies (phone width, or portrait), the page
   reloads once the resize has settled, so a narrow screen never keeps the desktop layout. */
(function () {
  if (!window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var qs = ['(max-width: 720px)', '(min-width: 721px) and (min-aspect-ratio: 1/1)'].map(function (q) { return window.matchMedia(q); });
  var atLoad = qs.map(function (m) { return m.matches; }), timer = 0;
  function check() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      if (qs.some(function (m, i) { return m.matches !== atLoad[i]; })) location.reload();
    }, 400);
  }
  qs.forEach(function (m) { if (m.addEventListener) m.addEventListener('change', check); else if (m.addListener) m.addListener(check); });
})();

/* ---------------- Reviews: live from Rivyo ----------------
   The product pages show their reviews with Rivyo, so this section asks Rivyo for the same reviews (the
   sectional's, which Rivyo shares across the Serena products): the score, the count, the star breakdown and
   five reviews, by default those with customer photos first. Each review shows its photos; they open full
   size. Until Rivyo answers, and if it cannot be reached, the server-rendered figures and "Review quote"
   blocks stay as they are. Review text is only ever set as text, never as HTML. */
(function () {
  var sec = document.getElementById('reviews');
  var pid = sec && sec.getAttribute('data-rivyo-product');
  if (!pid || !window.fetch) return;
  var page = document.getElementById('serena-page') || document.body;
  var WANT = 5, MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var base = 'https://app.rivyo.com/rivyo-app/V2/get-product-reviews?product_id=' + encodeURIComponent(pid) +
    '&product_handle=' + encodeURIComponent(sec.getAttribute('data-rivyo-handle') || '') +
    '&shop=' + encodeURIComponent(sec.getAttribute('data-rivyo-shop') || '') +
    '&page_url=' + encodeURIComponent(location.origin + location.pathname) +
    '&rating_val=&sort_by=' + encodeURIComponent(sec.getAttribute('data-rivyo-sort') || 'pictures-first') + '&page=';

  function get(n) {
    return fetch(base + n, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error('Rivyo ' + r.status);
      return r.json();
    }).then(function (j) {
      if (!j || !j.result || !j.result.review) throw new Error('Rivyo: no reviews');
      return j.result;
    });
  }
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function stars(n) {
    var s = el('span', 'stars', '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n));
    s.setAttribute('aria-label', n + ' out of 5 stars');
    return s;
  }
  function when(d) {                       // Rivyo sends MM/DD/YYYY
    var m = /^(\d{1,2})\/\d{1,2}\/(\d{4})$/.exec(d || '');
    return m && MONTHS[+m[1] - 1] ? MONTHS[+m[1] - 1] + ' ' + m[2] : (d || '');
  }

  /* full-size photos: one <dialog>, built on first use */
  var box = null, pics = [], at = 0, who = '', opener = null, byPointer = false;
  function paint() {
    var img = box.querySelector('img');
    img.src = pics[at].media_url;
    img.alt = 'Photo from ' + who + '’s review';
    box.querySelector('p').textContent = who + (pics.length > 1 ? ' · ' + (at + 1) + ' / ' + pics.length : '');
    box.querySelector('.rl-prev').hidden = box.querySelector('.rl-next').hidden = pics.length < 2;
  }
  function go(d) { at = (at + d + pics.length) % pics.length; paint(); }
  function show(list, i, name) {
    if (!window.HTMLDialogElement) { window.open(list[i].media_url, '_blank', 'noopener'); return; }
    if (!box) {
      box = el('dialog', 'rev-lightbox');
      box.setAttribute('aria-label', 'Customer photo');
      box.innerHTML = '<img alt=""><p></p><button type="button" class="rl-close" aria-label="Close">×</button>' +
        '<button type="button" class="rl-prev" aria-label="Previous photo">‹</button><button type="button" class="rl-next" aria-label="Next photo">›</button>';
      page.appendChild(box);
      box.querySelector('.rl-close').addEventListener('click', function () { box.close(); });
      box.querySelector('.rl-prev').addEventListener('click', function () { go(-1); });
      box.querySelector('.rl-next').addEventListener('click', function () { go(1); });
      box.addEventListener('click', function (e) { if (e.target === box) box.close(); });
      box.addEventListener('keydown', function (e) { if (e.key === 'ArrowLeft') go(-1); else if (e.key === 'ArrowRight') go(1); });
      /* the browser hands focus back to the thumbnail; keep that (and its ring) only for keyboard users */
      box.addEventListener('close', function () { if (byPointer && opener) opener.blur(); });
    }
    pics = list; at = i; who = name;
    paint();
    box.showModal();
  }
  function shot(list, i, name, cls) {
    var b = el('button', cls), img = el('img'), m = list[i];
    b.type = 'button';
    b.setAttribute('aria-label', 'Open photo ' + (i + 1) + ' of ' + list.length + ' from ' + name + '’s review');
    img.src = cls ? m.media_url : (m.media_thumbnail || m.media_url); img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
    b.appendChild(img);
    b.addEventListener('click', function (e) { opener = b; byPointer = e.detail > 0; show(list, i, name); });
    return b;
  }
  /* small cards: a row of thumbnails. The large review: its first photo fills the card's spare height (the card
     is as tall as the four small ones beside it), the others follow as thumbnails. */
  function media(r, name, max, big) {
    var list = (r.review_media || []).filter(function (m) { return m && m.media_url && (m.media_type || 'image') === 'image'; });
    if (!list.length) return null;
    var wrap = el('div', 'rev-media' + (big ? ' rev-media--big' : '')), row = wrap, from = big ? 1 : 0;
    if (big) {
      wrap.appendChild(shot(list, 0, name, 'rev-hero'));
      if (list.length < 2) return wrap;
      row = el('div', 'rev-thumbs');
      wrap.appendChild(row);
    }
    list.slice(from, from + max).forEach(function (m, k) {
      var b = shot(list, from + k, name);
      if (k === max - 1 && list.length > from + max) b.setAttribute('data-more', '+' + (list.length - from - max));
      row.appendChild(b);
    });
    return wrap;
  }
  function fill(fig, r, big) {
    var name = String(r.name || '').trim() || 'Customer';
    var rating = Math.max(1, Math.min(5, Math.round(+r.rating || 5)));
    var text = String(r.review_body || '').trim() || String(r.review_title || '').trim();
    var date = when(r.create_date);
    fig.textContent = '';
    fig.removeAttribute('data-shopify-editor-block');
    if (!big) fig.appendChild(stars(rating));
    var q = el('blockquote');
    q.appendChild(el('p', null, text));
    fig.appendChild(q);
    var m = media(r, name, 3, big);
    if (m) fig.appendChild(m);
    var cap = el('figcaption'), av = el('span', 'rev-av', name.charAt(0).toUpperCase()), who = el('span'), small = el('small');
    av.setAttribute('aria-hidden', 'true');
    who.appendChild(el('b', null, name));
    if (big) { small.appendChild(stars(rating)); if (date) small.appendChild(document.createTextNode(' · ' + date)); }
    else small.textContent = date;
    who.appendChild(small);
    cap.appendChild(av); cap.appendChild(who);
    fig.appendChild(cap);
  }
  function render(res, reviews) {
    var counts = {}, total = 0, sum = 0;
    (res.rating_badge_count || []).forEach(function (c) {
      var k = +c.rating, v = +c.total_review || 0;
      counts[k] = v; total += v; sum += k * v;
    });
    if (total) {
      var score = (sum / total).toFixed(2).replace(/0$/, '');   // 4.97, as Rivyo shows it
      page.querySelectorAll('[data-rv-score]').forEach(function (e) { e.textContent = score; });
      page.querySelectorAll('[data-rv-count]').forEach(function (e) { e.textContent = String(total); });
      var st = sec.querySelector('[data-rv-stars]');
      if (st) st.setAttribute('aria-label', score + ' out of 5 stars');
      sec.querySelectorAll('[data-rv-bar]').forEach(function (li) {
        var v = counts[+li.getAttribute('data-rv-bar')] || 0, bar = li.querySelector('i'), n = li.querySelector('em');
        if (bar) bar.style.setProperty('--w', (v / total * 100).toFixed(1) + '%');
        if (n) n.textContent = String(v);
      });
      page.querySelectorAll('[data-rv-show], [data-rv-bars]').forEach(function (e) { e.hidden = false; });
    }
    var wrap = sec.querySelector('.rev-wrap');
    if (!reviews.length || !wrap) return;
    var feature = wrap.querySelector('.rev-feature'), row = wrap.querySelector('.rev-row'), card = wrap.querySelector('.pcard');
    if (!feature) { feature = el('figure', 'rev-feature'); wrap.insertBefore(feature, row || card || null); }
    if (!row) { row = el('div', 'rev-row'); wrap.insertBefore(row, card || null); }
    fill(feature, reviews[0], true);
    var cards = row.querySelectorAll('.rev-card');
    reviews.slice(1).forEach(function (r, i) {
      var c = cards[i];
      if (!c) { c = el('figure', 'rev-card'); row.appendChild(c); }
      fill(c, r, false);
    });
    for (var i = Math.max(0, reviews.length - 1); i < cards.length; i++) cards[i].remove();
    sec.classList.add('is-rivyo');
  }
  function load() {
    get(1).then(function (first) {
      var list = (first.review.data || []).slice(), last = +first.review.last_page || 1, n = 1;
      function more() {
        if (list.length >= WANT || n >= last || n >= 4) return Promise.resolve();
        n++;
        return get(n).then(function (r) { list = list.concat(r.review.data || []); return more(); });
      }
      return more().then(function () { render(first, list.slice(0, WANT)); });
    }).catch(function () { /* Rivyo unreachable: the server-rendered reviews stay */ });
  }
  /* ask once the page has loaded (two small JSON requests), so the reviews are ready long before the visitor
     scrolls to them; the photos themselves still load only near the screen */
  var start = function () { (window.requestIdleCallback || function (f) { setTimeout(f, 200); })(load, { timeout: 2000 }); };
  if (document.readyState === 'complete') start(); else window.addEventListener('load', start);
})();
